import type { RubricCriterion } from '@/hooks/use-evaluation-rubrics-admin'

type ScoringConfig = {
  composite?: string
  structured_weight?: number
  semantic_weight?: number
  llm_threshold?: number
}

export const SCORING_PRESETS = {
  balanced: { label: 'Balanced', structured_weight: 0.6, semantic_weight: 0.4, llm_threshold: 0.7 },
  skills_heavy: { label: 'Skills-heavy', structured_weight: 0.75, semantic_weight: 0.25, llm_threshold: 0.65 },
  similarity_heavy: { label: 'Similarity-heavy', structured_weight: 0.4, semantic_weight: 0.6, llm_threshold: 0.75 },
} as const

export type ScoringPresetKey = keyof typeof SCORING_PRESETS

export type OrdinalLevelDef = {
  key: string
  label: string
  index: number
}

export type LevelDetail = {
  indicators?: string[]
  prompts?: string[]
}

export const STANDARD_ORDINAL_LEVELS: OrdinalLevelDef[] = [
  { key: 'developing', label: 'Developing', index: 1 },
  { key: 'foundational', label: 'Foundational', index: 2 },
  { key: 'proficient', label: 'Proficient', index: 3 },
  { key: 'advanced', label: 'Advanced', index: 4 },
  { key: 'expert', label: 'Expert', index: 5 },
]

export function slugifyCriterionKey(label: string, kind: RubricCriterion['kind'] = 'dimension'): string {
  const base = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
  if (!base) return kind === 'competency' ? 'focus_area' : 'criterion'
  return kind === 'competency' ? `focus_${base}` : base
}

export function scaleTypeOf(criterion: RubricCriterion): 'continuous' | 'ordinal' | 'boolean' {
  const type = (criterion.scale as { type?: string } | undefined)?.type
  if (type === 'ordinal' || type === 'boolean') return type
  if (criterion.kind === 'requirement') return 'boolean'
  return 'continuous'
}

export function buildPublishPayload(
  basePayload: Record<string, unknown>,
  criteria: RubricCriterion[],
  methodologyId: string,
  scoringOverride?: ScoringConfig,
): Record<string, unknown> {
  const dimensions = criteria
    .filter((c) => c.kind === 'dimension')
    .map((c) => ({ key: c.key, label: c.label, weight: c.weight ?? undefined }))

  const normalizedCriteria = criteria.map((c, index) => {
    const scaleType = scaleTypeOf(c)
    const defaultScale =
      scaleType === 'boolean'
        ? { type: 'boolean' }
        : scaleType === 'ordinal'
          ? c.scale ?? { type: 'ordinal', levels: STANDARD_ORDINAL_LEVELS }
          : { type: 'continuous', min: 0, max: 1 }
    return {
      key: c.key,
      kind: c.kind,
      label: c.label,
      definition: c.definition ?? '',
      weight: c.weight ?? undefined,
      scale: c.scale ?? defaultScale,
      levels: c.levels ?? {},
      metadata: c.metadata ?? {},
      component_skills: c.component_skills ?? [],
      sort_order: index,
    }
  })

  const scoring = scoringOverride ?? (basePayload.scoring as ScoringConfig | undefined) ?? {
    composite: 'weighted_mean',
    structured_weight: 0.6,
    semantic_weight: 0.4,
    llm_threshold: 0.7,
  }

  const evaluator = (basePayload.evaluator as Record<string, unknown> | undefined) ?? {
    pass1: 'employment_match_pass1',
    pass2: 'enrichment_v1',
    streaming: null,
  }

  return {
    ...basePayload,
    schema_version: '2',
    methodology_id: methodologyId,
    criteria: normalizedCriteria,
    dimensions,
    scoring,
    evaluator,
  }
}

export function validateCriteriaDraft(criteria: RubricCriterion[]): string | null {
  const keys = new Set<string>()
  for (const row of criteria) {
    if (!row.key.trim()) return 'Each criterion needs a key.'
    if (keys.has(row.key)) return `Duplicate criterion key: ${row.key}`
    keys.add(row.key)
    if (!row.label.trim()) return `Criterion ${row.key} needs a label.`

    const scaleType = scaleTypeOf(row)
    if (scaleType === 'ordinal') {
      const levels = ((row.scale as { levels?: OrdinalLevelDef[] } | undefined)?.levels) ?? []
      if (levels.length < 2) return `Criterion ${row.key}: named levels need at least 2 levels.`
      const levelKeys = new Set<string>()
      const levelIndices = new Set<number>()
      for (const level of levels) {
        if (!level.key?.trim()) return `Criterion ${row.key}: each level needs a key.`
        if (levelKeys.has(level.key)) return `Criterion ${row.key}: duplicate level key '${level.key}'.`
        levelKeys.add(level.key)
        if (levelIndices.has(level.index)) {
          return `Criterion ${row.key}: duplicate level index ${level.index}.`
        }
        levelIndices.add(level.index)
      }
    }
  }
  const dimensionWeight = criteria
    .filter((c) => c.kind === 'dimension' && scaleTypeOf(c) !== 'boolean')
    .reduce((sum, c) => sum + (c.weight ?? 0), 0)
  if (dimensionWeight > 0 && Math.abs(dimensionWeight - 1) > 0.05) {
    return `Dimension weights sum to ${dimensionWeight.toFixed(2)} (expected ~1.0).`
  }
  return null
}

export function dimensionWeightTotal(criteria: RubricCriterion[]): number {
  return criteria
    .filter((c) => c.kind === 'dimension' && scaleTypeOf(c) !== 'boolean')
    .reduce((sum, c) => sum + (c.weight ?? 0), 0)
}

export function weightTotalForKind(criteria: RubricCriterion[], kind: string): number {
  return criteria
    .filter((c) => c.kind === kind && scaleTypeOf(c) !== 'boolean')
    .reduce((sum, c) => sum + (c.weight ?? 0), 0)
}

export function normalizeWeightsForKind(
  criteria: RubricCriterion[],
  kind: string,
): RubricCriterion[] {
  const indices = criteria
    .map((c, i) => ({ c, i }))
    .filter(({ c }) => c.kind === kind && scaleTypeOf(c) !== 'boolean')
  if (indices.length === 0) return criteria
  const equal = 1 / indices.length
  return criteria.map((c, i) =>
    indices.some((row) => row.i === i) ? { ...c, weight: Math.round(equal * 100) / 100 } : c,
  )
}

export type CriteriaDiff = {
  added: RubricCriterion[]
  removed: RubricCriterion[]
  changed: Array<{ key: string; before: RubricCriterion; after: RubricCriterion }>
}

export function diffCriteria(published: RubricCriterion[], draft: RubricCriterion[]): CriteriaDiff {
  const beforeMap = new Map(published.map((c) => [c.key, c]))
  const afterMap = new Map(draft.map((c) => [c.key, c]))
  const added = draft.filter((c) => !beforeMap.has(c.key))
  const removed = published.filter((c) => !afterMap.has(c.key))
  const changed = draft
    .filter((c) => {
      const prev = beforeMap.get(c.key)
      return prev && JSON.stringify(prev) !== JSON.stringify(c)
    })
    .map((c) => ({ key: c.key, before: beforeMap.get(c.key)!, after: c }))
  return { added, removed, changed }
}

export function newCriterionDraft(kind: RubricCriterion['kind'] = 'dimension'): RubricCriterion {
  const suffix = Math.random().toString(36).slice(2, 6)
  const scale =
    kind === 'requirement'
      ? { type: 'boolean' }
      : kind === 'competency'
        ? { type: 'ordinal', levels: STANDARD_ORDINAL_LEVELS }
        : { type: 'continuous', min: 0, max: 1 }
  return {
    key: kind === 'competency' ? `focus_${suffix}` : kind === 'requirement' ? `req_${suffix}` : `dim_${suffix}`,
    kind,
    label: '',
    definition: '',
    weight: kind === 'requirement' ? null : kind === 'dimension' ? 0.1 : 0.05,
    scale,
    levels: {},
    component_skills: [],
  }
}

export function buildPreviewAnalysisPayload(
  criteria: RubricCriterion[],
  skillDomains: Array<{ key?: string; label?: string; skills?: string[] }>,
  methodologyId: string,
) {
  const criteriaResults = criteria.map((c) => {
    const scaleType = scaleTypeOf(c)
    const base = {
      key: c.key,
      kind: c.kind,
      label: c.label,
      reasoning: c.definition
        ? `Preview: ${c.definition.slice(0, 80)}`
        : 'Preview score for rubric draft.',
      weight: c.weight ?? undefined,
    }
    if (scaleType === 'boolean') {
      return { ...base, met: true, score: 1 }
    }
    if (scaleType === 'ordinal') {
      const levels = ((c.scale as { levels?: OrdinalLevelDef[] } | undefined)?.levels) ?? []
      const mid = levels[Math.floor(levels.length / 2)] ?? { key: 'proficient', index: 3, label: 'Proficient' }
      return {
        ...base,
        level_key: mid.key,
        level_index: mid.index,
        score: 0.72,
      }
    }
    return { ...base, score: 0.72 }
  })
  const dimensions = criteria
    .filter((c) => c.kind === 'dimension')
    .map((c) => ({
      key: c.key,
      label: c.label,
      score: 0.72,
      reasoning: 'Preview dimension score.',
      weight: c.weight ?? undefined,
    }))

  return {
    schema_version: '2',
    methodology_id: methodologyId,
    headline: 'Preview: Good potential match',
    dimensions,
    criteria_results: criteriaResults,
    requirements: { required_skills: [], preferred_skills: [] },
    findings: { highlights: ['Preview highlight from draft rubric'], red_flags: [] },
    narrative: {
      summary: 'This is a preview of how analysis reports will look with your current criteria.',
    },
    scoring_trace: {
      component_scores: { structured: 0.7, semantic: 0.75 },
      final_score: 0.72,
      was_llm_judged: true,
    },
    rubric_snapshot: {
      methodology_id: methodologyId,
      skill_domains: skillDomains,
    },
  }
}
